<template>
	<div
		v-scroll-container="{
			target: '.js-scroll-wrapper',
			disableScrollShadow: false,
		}"
		class="action-tabs"
	>
		<Tab
			v-if="!isMobileWebApp"
			class="desktop-nav"
			:active-tab="activeTab"
			:tabs="tabs"
			:hide-header="hasOneTab"
			:modifiers="['simple']"
			@tab-change="onTabChange"
		>
			<!-- Lineup Tab -->
			<template
				v-if="hasLineup"
				#tab-details-lineup
			>
				<LineupBlock />
			</template>
			<!-- TagTemplates Tab -->
			<template
				v-if="hasTagTemplates"
				#tab-details-tagTemplates
			>
				<TagTemplateList />
			</template>
			<!-- ClipList Tab -->
			<template
				v-if="hasClips"
				#tab-details-clips
			>
				<ClipList />
			</template>
			<!-- Videos Tab -->
			<template #tab-details-videos>
				<Videos v-if="playerType === playerTypes.CONTEXT || playerType === playerTypes.LEAGUE" />
			</template>
			<!-- Matchday Tab -->
			<template #tab-details-matchday>
				<MatchdayBlock
					v-if="playerType === playerTypes.LEAGUE"
					:show-league-header="hasOneTab"
				/>
			</template>
		</Tab>
		<Tab
			v-if="isMobileWebApp && !isMobileLandscape"
			:tabs="tabs"
			:active-tab="activeTab"
			:hide-header="hasOneTab"
			:class-name="'mobile-nav'"
			:modifiers="['simple']"
			:sticky-header="true"
			@tab-change="onTabChange"
		>
			<!-- Lineup Tab -->
			<template
				v-if="hasLineup"
				#tab-details-lineup
			>
				<LineupBlock />
			</template>
			<!-- TagTemplates Tab -->
			<template
				v-if="hasTagTemplates"
				#tab-details-tagTemplates
			>
				<TagTemplateList />
			</template>
			<!-- ClipList Tab -->
			<template
				v-if="hasClips"
				#tab-details-clips
			>
				<ClipList />
			</template>
			<!-- Videos Tab -->
			<template #tab-details-videos>
				<Videos v-if="playerType === playerTypes.CONTEXT || playerType === playerTypes.LEAGUE" />
			</template>
			<!-- Matchday Tab -->
			<template #tab-details-matchday>
				<MatchdayBlock
					v-if="playerType === playerTypes.LEAGUE"
					:show-league-header="hasOneTab"
				/>
			</template>
		</Tab>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import playerTypes from '@videotools/js/constants/playerTypes';
import Tab from '@components/tab';
import TagTemplateList from '@videotools/js/components/clip-list/TagTemplateList';
import ClipList from '@videotools/js/components/clip-list/ClipList';
import LineupBlock from '@videotools/js/blocks/LineupBlock';
import MatchdayBlock from '@videotools/js/blocks/Matchday';
import Videos from '@components/Videos';
import { events as HOTKEYS_EVENTS } from '@videotools/js/events/modules/player-hotkeys';
import { bus } from '@videotools/js/events';
import { PARENT_HIGHLIGHT, PARENT_OTHER } from '@videotools/js/constants/categories';
import { SportName } from '@shared/enums/SportName';
import VScrollContainer from '@shared/directives/v-scroll-container';

const events = [
	HOTKEYS_EVENTS.HOTKEY_NEXT_TAB,
	HOTKEYS_EVENTS.HOTKEY_PREVIOUS_TAB,
];

export default {
	name:       'ActionTabsBlock',
	components: {
		TagTemplateList,
		ClipList,
		LineupBlock,
		Tab,
		MatchdayBlock,
		Videos,
	},
	directives: {
		scrollContainer: VScrollContainer,
	},
	props: {
		rememberScrollPosition: {
		  type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			playerTypes,
			activeTab:       null,
			scrollPositions: {},
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			playerType:        'app/playerType',
			isMobileLandscape: 'device/isMobileLandscape',
			lineup:            'event/lineup',
			tags:              'event/playerTags',
			tagTemplates:      'event/tagTemplates',
			event:             'event/event',
			videos:            'videos/videos',
			preferences:       'sharedUser/preferences',
		}),
		noSpoilerMode() {
			return this.preferences?.noSpoilerMode ?? false;
		},
		hasOneTab() {
			return this.tabs.length === 1;
		},
		// tabs
		tabs() {
			const tabs = {
				tagTemplates: {
					icon:    'numbered-list',
					enabled: false,
				},
				clips: {
					icon:    'movie',
					enabled: false,
				},
				lineup: {
					icon:    'users-wm',
					enabled: false,
				},
				matchday: {
					icon:    'calendar-event',
					enabled: false,
				},
				videos: {
					icon:    'video-playlist',
					enabled: false,
				},
			};

			if (this.hasTagTemplates) tabs.tagTemplates.enabled = true;
			if (this.hasLineup && !this.isVolleyball) tabs.lineup.enabled = true;
			if (this.hasClips && !this.isVolleyball && !this.hasTagTemplates && !this.noSpoilerMode) tabs.clips.enabled = true;
			switch (this.playerType) {
			case playerTypes.LEAGUE:
				if ([PARENT_HIGHLIGHT, PARENT_OTHER].includes(this.event.categoryParent)) {
					tabs.videos.enabled = true;
				} else {
					tabs.matchday.enabled = true;
				}
				break;
			case playerTypes.CONTEXT:
				tabs.videos.enabled = true;
				break;
			default:
				break;
			}

			return Object.entries(tabs)
				.filter(([_k, v]) => v.enabled)
				.reduce((acc, [k, v]) => {
					acc.push({ key: k, icon: v.icon });
					return acc;
				}, []);
		},
		hasLineup() {
			return Array.isArray(this.lineup) && this.lineup.length > 0;
		},
		isVolleyball() {
			return this.event?.sport?.name === SportName.VOLLEYBALL;
		},
		hasClips() {
			return Array.isArray(this.tags) && this.tags.some(tag => tag.timestamp > 0);
		},
		hasTagTemplates() {
			return Array.isArray(this.tagTemplates) && this.tagTemplates.length > 0;
		},
	},
	watch: {
		tabs(newTabs, oldTabs) {
			// TODO: handle case where clip list appears and dissapears dynamically?
			if (oldTabs.length === newTabs.length) return;
			const clipsTab = newTabs.find(tab => tab.key === 'clips');
			const tagTemplatesTab = newTabs.find(tab => tab.key === 'tagTemplates');
			if (tagTemplatesTab) {
				this.activeTab = tagTemplatesTab.key;
			} else if (clipsTab) {
				 this.activeTab = clipsTab.key;
			}
			else this.activeTab = newTabs[0]?.key;
		},
	},
	mounted() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	destroyed() {
		// subscribe to external events
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		onTabChange(tabKey) {
			if (this.rememberScrollPosition) {
			  this.storeScrollPosition(this.activeTab);
			  this.$nextTick(() => this.updateScrollPosition(tabKey));
			}
			this.activeTab = tabKey;
		},
		[`BUS-${HOTKEYS_EVENTS.HOTKEY_NEXT_TAB}`]() {
			const tabIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);
			let newTab;
			if (tabIndex === this.tabs.length - 1) newTab = this.tabs[0];
			else newTab = this.tabs[tabIndex + 1];

			this.activeTab = newTab.key;
		},
		[`BUS-${HOTKEYS_EVENTS.HOTKEY_PREVIOUS_TAB}`]() {
			const tabIndex = this.tabs.findIndex(tab => tab.key === this.activeTab);

			let newTab;
			if (tabIndex === 0) newTab = this.tabs[this.tabs.length - 1];
			else newTab = this.tabs[tabIndex - 1];

			this.activeTab = newTab.key;
		},
		updateScrollPosition(tabKey) {
			if (Object.keys(this.scrollPositions).includes(tabKey)) {
			  window.scrollTo({ top: this.scrollPositions[tabKey] });
			}
		},
		storeScrollPosition(tabKey) {
		  if (tabKey) {
				this.scrollPositions[tabKey] = window.scrollY;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.action-tabs {
	width: 100%;
	height: 100%;
	padding-top: $sp4;
	overflow: hidden;

	@include size(md-up) {
		padding-top: 0;
	}
}
</style>
